import React, { useState, useEffect, useCallback } from 'react';
import {
    Zap, LayoutDashboard, Settings,
    Server, Trash2, Plus, Users, CreditCard, Bot, Image, ShoppingCart, Flame, Copy, X, Pencil, Banknote, Wallet,
    ChevronDown, ChevronRight, MessageSquare, Crown, Network
} from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogOut } from 'lucide-react';
import logoPro from '../logo_pro.png';
import api from '../services/api';

interface Account {
    id: number | string;
    phone: string;
    is_active: boolean;
    status: string;
}

interface SidebarProps {
    accounts: Account[];
    selectedAccountId: number | string | null;
    onSelectAccount: (id: number | string) => void;
    onAddAccount: () => void;
    onDeleteAccount: (e: React.MouseEvent, id: number | string) => void;
    onEditAccount?: (acc: Account) => void;
    onOpenIDFinder: () => void;
    isOpen?: boolean;
    onClose?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
    accounts,
    selectedAccountId,
    onSelectAccount,
    onAddAccount,
    onDeleteAccount,
    onEditAccount,
    onOpenIDFinder,
    isOpen = false,
    onClose
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout, isFreePlan } = useAuth();
    const isActive = (path: string) => location.pathname === path ? 'active' : '';

    const [expandedAccId, setExpandedAccId] = useState<number | string | null>(null);
    const [accBots, setAccBots] = useState<any[]>([]);
    const [accGroups, setAccGroups] = useState<any[]>([]);
    const [loadingDetails, setLoadingDetails] = useState(false);

    const fetchAccountDetails = useCallback(async (accId: number | string) => {
        setLoadingDetails(true);
        try {
            const [botsRes, groupsRes] = await Promise.all([
                api.get(`/bots?account_id=${accId}`),
                api.get(`/groups?account_id=${accId}`)
            ]);
            setAccBots(Array.isArray(botsRes.data) ? botsRes.data : botsRes.data?.bots || []);
            setAccGroups(Array.isArray(groupsRes.data) ? groupsRes.data : groupsRes.data?.groups || []);
        } catch {
            setAccBots([]);
            setAccGroups([]);
        } finally {
            setLoadingDetails(false);
        }
    }, []);

    useEffect(() => {
        if (expandedAccId) {
            fetchAccountDetails(expandedAccId);
        }
    }, [expandedAccId, fetchAccountDetails]);

    const toggleExpand = (accId: number | string) => {
        if (expandedAccId === accId) {
            setExpandedAccId(null);
            setAccBots([]);
            setAccGroups([]);
        } else {
            setAccBots([]);
            setAccGroups([]);
            setExpandedAccId(accId);
        }
    };

    return (
        <div className={`sidebar pro-sidebar ${isOpen ? 'open' : ''}`}>
            {/* Mobile Close Button */}
            <button className="mobile-close-btn" onClick={onClose}>
                <X size={24} />
            </button>

            <div className="brand-logo" style={{ padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                <img src={logoPro} alt="Hotlize Logo" style={{ maxWidth: '100%', height: 'auto' }} />
            </div>

            <nav className="sidebar-nav">
                <Link to="/" className={`nav-item ${isActive('/')}`} onClick={onClose}>
                    <LayoutDashboard size={20} />
                    <span>Dashboard</span>
                </Link>

                <Link to="/repasse" className={`nav-item ${isActive('/repasse')}`} onClick={onClose}>
                    <Flame size={20} />
                    <span>Repasse</span>
                </Link>

                <div className="nav-group-divider"></div>

                <Link to="/hot-cloner" className={`nav-item ${isActive('/hot-cloner')}`} onClick={onClose}>
                    <Copy size={20} />
                    <span>HotCloner</span>
                </Link>

                <Link to="/bots" className={`nav-item ${isActive('/bots')}`} onClick={onClose}>
                    <Bot size={20} />
                    <span>Meus Bots</span>
                </Link>

                <Link to="/plans" className={`nav-item ${isActive('/plans')}`} onClick={onClose}>
                    <CreditCard size={20} />
                    <span>Planos</span>
                </Link>

                <Link to="/financeiro" className={`nav-item ${isActive('/financeiro')}`} onClick={onClose}>
                    <Wallet size={20} />
                    <span>Financeiro</span>
                </Link>

                <Link to="/groups" className={`nav-item ${isActive('/groups')}`} onClick={onClose}>
                    <Users size={20} />
                    <span>Grupos</span>
                </Link>

                <Link to="/media" className={`nav-item ${isActive('/media')}`} onClick={onClose}>
                    <Image size={20} />
                    <span>Mídias</span>
                </Link>

                <Link to="/checkout" className={`nav-item ${isActive('/checkout')}`} onClick={onClose}>
                    <ShoppingCart size={20} />
                    <span>Checkout</span>
                </Link>

                <div className="nav-group-divider"></div>

                <Link to="/networking" className={`nav-item ${isActive('/networking')}`} onClick={onClose}>
                    <Network size={20} />
                    <span>Grupo de Networking</span>
                </Link>

                <Link to="/assinar" className={`nav-item ${isActive('/assinar')}`} onClick={onClose}>
                    <Crown size={20} />
                    <span>Assinar Plano</span>
                </Link>
            </nav>

            <div className="sidebar-accounts">
                <h2 className="section-title">Contas Telegram</h2>

                <div className="accounts-list">
                    {accounts.map(acc => {
                        const isExpanded = expandedAccId === acc.id;
                        return (
                            <div key={acc.id}>
                                <div
                                    className={`account-item ${selectedAccountId === acc.id ? 'active' : ''}`}
                                    onClick={() => { onSelectAccount(acc.id); toggleExpand(acc.id); }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                                        <div className={`status-indicator ${acc.status === 'online' ? 'online' : ''}`}>
                                            <Server size={14} />
                                        </div>
                                        <div className="account-info" style={{ minWidth: 0 }}>
                                            <span className="account-phone">{acc.phone}</span>
                                            <span className="account-status-text">{acc.status === 'online' ? 'Online' : 'Offline'}</span>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <div className="account-actions">
                                            {onEditAccount && (
                                                <span className="edit-acc-btn" onClick={(e) => { e.stopPropagation(); onEditAccount(acc); }} title="Alterar número">
                                                    <Pencil size={14} />
                                                </span>
                                            )}
                                            <span className="delete-acc-btn" onClick={(e) => onDeleteAccount(e, acc.id)} title="Excluir conta">
                                                <Trash2 size={14} />
                                            </span>
                                        </div>
                                        <div style={{ color: '#666', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                                            <ChevronDown size={14} />
                                        </div>
                                    </div>
                                </div>

                                {isExpanded && (
                                    <div style={{
                                        padding: '4px 0 8px 28px',
                                        display: 'flex', flexDirection: 'column', gap: '2px',
                                        animation: 'fadeIn 0.2s ease'
                                    }}>
                                        {loadingDetails ? (
                                            <span style={{ fontSize: '11px', color: '#555', padding: '6px 0' }}>Carregando...</span>
                                        ) : (
                                            <>
                                                {accBots.length > 0 && (
                                                    <div style={{ marginBottom: '4px' }}>
                                                        <span style={{ fontSize: '10px', color: '#666', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Bots ({accBots.length})</span>
                                                        {accBots.map((bot: any) => (
                                                            <div
                                                                key={bot.id}
                                                                onClick={(e) => { e.stopPropagation(); navigate('/bots'); }}
                                                                style={{
                                                                    display: 'flex', alignItems: 'center', gap: '8px',
                                                                    padding: '5px 8px', borderRadius: '6px', cursor: 'pointer',
                                                                    transition: 'background 0.15s',
                                                                    fontSize: '12px', color: '#ccc'
                                                                }}
                                                                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.04)')}
                                                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                                                            >
                                                                <Bot size={12} style={{ color: bot.status === 'online' ? '#22c55e' : '#666', flexShrink: 0 }} />
                                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{bot.name || `@${bot.username}`}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                {accGroups.length > 0 && (
                                                    <div>
                                                        <span style={{ fontSize: '10px', color: '#666', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Grupos ({accGroups.length})</span>
                                                        {accGroups.map((group: any) => (
                                                            <div
                                                                key={group.id}
                                                                onClick={(e) => { e.stopPropagation(); navigate('/groups'); }}
                                                                style={{
                                                                    display: 'flex', alignItems: 'center', gap: '8px',
                                                                    padding: '5px 8px', borderRadius: '6px', cursor: 'pointer',
                                                                    transition: 'background 0.15s',
                                                                    fontSize: '12px', color: '#ccc'
                                                                }}
                                                                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.04)')}
                                                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                                                            >
                                                                <MessageSquare size={12} style={{ color: '#3b82f6', flexShrink: 0 }} />
                                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{group.title || group.name}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                {accBots.length === 0 && accGroups.length === 0 && (
                                                    <span style={{ fontSize: '11px', color: '#555', padding: '6px 0', fontStyle: 'italic' }}>Nenhum bot ou grupo</span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <button
                    className="add-account-btn"
                    onClick={() => {
                        if (isFreePlan) {
                            onClose?.();
                            navigate('/assinar');
                        } else {
                            onAddAccount();
                            onClose?.();
                        }
                    }}
                >
                    <Plus size={16} /> Adicionar Conta
                </button>
            </div>

            <div className="sidebar-footer">
                <div className="user-profile">
                    <div className="user-avatar">{user?.full_name?.[0] || user?.email?.[0] || 'U'}</div>
                    <div className="user-details">
                        <span>{user?.full_name || user?.email?.split('@')[0] || 'Usuário'}</span>
                        <small>{user?.role === 'admin' ? 'Master Admin' : 'Pro Plan'}</small>
                    </div>
                    <button
                        onClick={logout}
                        className="ml-auto text-gray-500 hover:text-red-500 transition-colors"
                        title="Sair"
                    >
                        <LogOut size={18} />
                    </button>
                </div>
            </div>
        </div>
    );
};
